/**
 * External dependencies
 */
import classnames from 'classnames';
import { isUndefined, pickBy, get, isEmpty, isArray, isString } from 'lodash';
import { Swiper, SwiperSlide } from 'swiper/react';

/**
 * WordPress dependencies.
 */
import { useEffect, RawHTML, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { compose } from '@wordpress/compose';
import { withSelect } from '@wordpress/data';
import { Notice } from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import { applyFilters } from '@wordpress/hooks';

/**
 * Internal dependencies
 */
import Inspector from './inspector';
const { is_woocommerce_activated: isWoocommerceActivated } = window.gutenify_block_data;
import blockInfo from './constants';
const { hookPrefix } = blockInfo;

// import Swiper core and required modules
import SwiperCore, { Pagination, Navigation, Autoplay } from 'swiper/core';

// install Swiper modules
SwiperCore.use( [ Pagination, Navigation, Autoplay ] );

function DisplayPost( props ) {
	const { post, attributes } = props;
	const { displayFeaturedImage } = attributes;
	const { permalink, name, price_html: priceHtml, grouped_products: groupedProducts, variations, button_text: buttonText, on_sale: onSale } = post;
	// Featured image.
	const featuredImageUrl = post?.images[ 0 ] ? post.images[ 0 ].src : 'http://wpnext.local/wp-content/uploads/woocommerce-placeholder.png';

	let _buttonText = __( 'Add to cart' );
	if ( buttonText ) {
		_buttonText = buttonText;
	} else if ( groupedProducts.length ) {
		_buttonText = __( 'View products' );
	} else if ( variations.length ) {
		_buttonText = __( 'Select Options' );
	} else if ( '' === priceHtml ) {
		_buttonText = __( 'Read more' );
	}
	return <div className="gutenify--wc-product--item has-no-hover-shadow-dark">
		<div className="gutenify--wc-product--item-wrapper">
			{ displayFeaturedImage && featuredImageUrl &&
			<div className="gutenify--wc-product--thumb">
				<a className="image-hover-zoom" href={ permalink } onClick={ ( event ) => event.preventDefault() }>
					<img src={ featuredImageUrl } alt={ name } />
				</a>
				{ onSale && <div className="gutenify--wc-product--onsale">
					<span aria-hidden="true">{ __( 'Sale' ) }</span>
					<span className="screen-reader-text">{ __( 'Product on sale' ) }</span>
				</div> }
			</div>
			}
			<div className="gutenify--wc-product--item-content">
				<h3 className="gutenify--wc-product--title">
					<a href={ permalink } onClick={ ( event ) => event.preventDefault() } rel="bookmark">
						<RawHTML key="html">{ name }</RawHTML>
					</a>
				</h3>
				<RawHTML key="html" className="gutenify--wc-product--price">{ priceHtml }</RawHTML>
				<div className="wp-block-button wc-block-grid__product-add-to-cart">
					<a href={ permalink }className="wp-block-button__link add_to_cart_button" tabIndex="-1">
						{ _buttonText }
					</a>
				</div>

			</div>
		</div>
	</div>;
}

const Edit = ( props ) => {
	const { isSelected, className, attributes, currentPost } = props;
	const { columns, layout, blockClientId, query, spaceBetween, hasNavigation, hasPagination } = attributes;
	const [ latestPosts, setLatestPosts ] = useState( [] );
	useEffect( () => {
		const _query = {
			order: query.order,
			orderby: query.orderBy,
			per_page: query.numberOfItems,
			exclude: currentPost.id,
		};

		if ( query?.tax ) {
			for ( const property in query.tax ) {
				let _property = 'product_cat' === property ? 'category' : property;
				_property = 'product_tag' === _property ? 'tag' : _property;
				if ( isArray( query.tax[ property ] ) && query.tax[ property ].length > 0 ) {
					_query[ _property ] = query.tax[ property ].join( ',' );
				}
			}
		}

		if ( query?.authorIds ) {
			_query.author = query.authorIds;
		}

		const latestPostsQuery = pickBy( _query, ( value ) => {
			if ( ! isArray( value ) && isUndefined( value ) ) {
				return false;
			} else if ( isArray( value ) && 1 > value.length ) {
				return false;
			}
			return true;
		} );

		const path = addQueryArgs( '/wc/v3/products', latestPostsQuery );

		apiFetch( { path } ).then( ( posts ) => {
			setLatestPosts( posts );
		} );
	}, [ query ] );
	let wrapperClasses = [
		className,
		`gutenify--wc-product-carousel-${ layout }`,
		`gutenify--wc-product-carousel-col-${ columns }`,
		`gutenify-section-${ blockClientId }`,
	];
	wrapperClasses = classnames( applyFilters( `gutenify--${ hookPrefix }--wrapper-class`, wrapperClasses, props ) );

	const hasPosts = Array.isArray( latestPosts ) && latestPosts.length;

	return <>
		{ isSelected && <Inspector { ...props } /> }
		<div className={ wrapperClasses }>
			{ isWoocommerceActivated && ! hasPosts ? <>{ __( 'No products found.' ) }</> : <Swiper
				spaceBetween={ spaceBetween }
				slidesPerView={ columns }
				loop={ true }
				navigation={ hasNavigation }
				pagination={ hasPagination }
				scrollbar={ { draggable: true } }
				// onClick={ () => selectBlock() }
				autoplay={ false }
				className="gutenify-wc-product-carousel"
				allowTouchMove={ false }
				rebuildOnUpdate={ true }
			>
				{ latestPosts.map( ( post ) => {
					return <SwiperSlide className="gutenify-wc-product-carousel-item gutenify-wc-product-carousel-item-wrapper inner-block-shadow" key={ post.id || post.link }>
						<DisplayPost post={ post } { ...props } />
					</SwiperSlide>;
				} ) }
			</Swiper> }

			{ ! isWoocommerceActivated && <><Notice status="error" isDismissible={ false }>{ __( 'WooCommerce is not activated.' ) }</Notice></> }

		</div>
	</>;
};

export default compose( [
	withSelect( ( select ) => {
		const { getCurrentPost } = select( 'core/editor' );
		const currentPost = getCurrentPost();

		return {
			currentPost,
		};
	} ),
] )( Edit );

